<template>
  <div class="standard-pagination-container">
    <div class="pagination-content">
      <!-- Informações de linhas por página -->
      <div class="pagination-info">
        <span class="pagination-text">Linhas por página:</span>
        <v-select
          v-model="internalItemsPerPage"
          :items="itemsPerPageOptions"
          outlined
          dense
          hide-details
          class="pagination-select"
          @change="handleItemsPerPageChange"
        />
      </div>

      <!-- Informações de paginação -->
      <div class="pagination-summary">
        <span class="pagination-text">
          {{ startItem }}–{{ endItem }} de {{ totalItems }} registros
        </span>
      </div>

      <!-- Controles de navegação -->
      <div class="pagination-controls">
        <!-- Botões circulares para desktop -->
        <div class="pagination-buttons-desktop">
          <v-btn
            icon
            small
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
            class="pagination-btn"
            :class="{ 
              'pagination-btn-disabled': currentPage === 1,
              'pagination-btn-enabled': currentPage > 1
            }"
            aria-label="Página anterior"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          
          <button
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
            class="pagination-text-link"
            :class="{ 
              'pagination-text-link-disabled': currentPage === 1,
              'pagination-text-link-enabled': currentPage > 1
            }"
            aria-label="Página anterior"
          >
            Anterior
          </button>
          
          <span class="pagination-text pagination-separator">|</span>
          
          <button
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
            class="pagination-text-link"
            :class="{ 
              'pagination-text-link-disabled': currentPage >= totalPages,
              'pagination-text-link-enabled': currentPage < totalPages
            }"
            aria-label="Próxima página"
          >
            Próximo
          </button>
          
          <v-btn
            icon
            small
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
            class="pagination-btn"
            :class="{ 
              'pagination-btn-disabled': currentPage >= totalPages,
              'pagination-btn-enabled': currentPage < totalPages
            }"
            aria-label="Próxima página"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
        
        <!-- Links de texto para mobile -->
        <div class="pagination-links-mobile">
          <button
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
            class="pagination-link"
            :class="{ 
              'pagination-link-disabled': currentPage === 1,
              'pagination-link-enabled': currentPage > 1
            }"
            aria-label="Página anterior"
          >
            Anterior
          </button>
          
          <span class="pagination-separator-mobile">|</span>
          
          <button
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
            class="pagination-link"
            :class="{ 
              'pagination-link-disabled': currentPage >= totalPages,
              'pagination-link-enabled': currentPage < totalPages
            }"
            aria-label="Próxima página"
          >
            Próximo
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StandardPagination',
  props: {
    currentPage: {
      type: Number,
      required: true
    },
    totalItems: {
      type: Number,
      required: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [10, 20, 50]
    }
  },
  data() {
    return {
      internalItemsPerPage: this.itemsPerPage
    };
  },
  watch: {
    itemsPerPage(newValue) {
      this.internalItemsPerPage = newValue;
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.internalItemsPerPage);
    },
    startItem() {
      return (this.currentPage - 1) * this.internalItemsPerPage + 1;
    },
    endItem() {
      const end = this.currentPage * this.internalItemsPerPage;
      return end > this.totalItems ? this.totalItems : end;
    }
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$emit('page-changed', page);
      }
    },
    handleItemsPerPageChange() {
      this.$emit('items-per-page-changed', this.internalItemsPerPage);
    }
  }
}
</script>

<style scoped>
.standard-pagination-container {
  padding: 8px 12px;
  background-color: #FFFFFF;
  border-top: 1px solid #E5E7EB;
  border-radius: 0 0 8px 8px;
  max-width: 100%;
  overflow: hidden;
}

.pagination-content {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  max-width: 100%;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-self: start;
}

.pagination-summary {
  display: flex;
  align-items: center;
  justify-self: center;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-self: end;
}

.pagination-text {
  color: #6B7280;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
}

.pagination-select {
  min-width: 60px;
  max-width: 80px;
}

.pagination-select :deep(.v-input__control) {
  min-height: 32px;
}

.pagination-select :deep(.v-input__slot) {
  border-radius: 6px;
  border: 1px solid #D1D5DB;
  background-color: #FFFFFF;
}

.pagination-select :deep(.v-input__slot:hover) {
  border-color: #16A34A;
}

.pagination-select :deep(.v-input__slot.v-input--is-focused) {
  border-color: #16A34A;
  border-width: 2px;
}

.pagination-btn {
  color: #6B7280;
  background-color: transparent;
  border: 1px solid #E5E7EB;
  transition: all 0.2s ease;
  min-width: 28px;
  height: 28px;
  border-radius: 50%;
}

.pagination-btn-enabled {
  color: #374151 !important;  /* Mesma cor do texto "Linhas por página" */
  border-color: #16A34A !important;
  background-color: #FFFFFF !important;
}

.pagination-btn-enabled:hover {
  color: #16A34A !important;  /* Verde no hover */
  background-color: rgba(22, 163, 74, 0.1) !important;
  border-color: #15803D !important;
  transform: translateY(-1px);
}

.pagination-btn-disabled {
  color: #D1D5DB !important;
  border-color: #E5E7EB !important;
  background-color: #F9FAFB !important;
  cursor: not-allowed !important;
  opacity: 0.6 !important;
}

.pagination-btn-disabled:hover {
  transform: none !important;
  background-color: #F9FAFB !important;
  border-color: #E5E7EB !important;
}

/* Sobrescrever estilos padrão do Vuetify */
.pagination-btn:deep(.v-btn--disabled) {
  color: #D1D5DB !important;
}

.pagination-btn-enabled:deep(.v-btn__content) {
  color: #374151 !important;  /* Mesma cor do texto padrão */
}

.pagination-btn-enabled:hover:deep(.v-btn__content) {
  color: #16A34A !important;  /* Verde no hover */
}

.pagination-separator {
  color: #374151;  /* Mesma cor do texto padrão em vez de cinza claro */
  font-weight: 400;
  font-size: 0.8rem;
}

.pagination-text-link {
  background: none;
  border: none;
  color: #374151;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0 4px;
  transition: all 0.2s ease;
  font-family: inherit;
}

.pagination-text-link-enabled {
  color: #374151 !important;
}

.pagination-text-link-enabled:hover {
  color: #16A34A !important;
}

.pagination-text-link-disabled {
  color: #D1D5DB !important;
  cursor: not-allowed !important;
}

/* Controle de visibilidade desktop/mobile */
.pagination-buttons-desktop {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-links-mobile {
  display: none;
}

/* Estilos para links de texto no mobile */
.pagination-link {
  background: none;
  border: none;
  color: #374151;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.pagination-link-enabled {
  color: #16A34A !important;
}

.pagination-link-enabled:hover {
  background-color: rgba(22, 163, 74, 0.1);
  color: #15803D !important;
}

.pagination-link-disabled {
  color: #D1D5DB !important;
  cursor: not-allowed !important;
}

.pagination-separator-mobile {
  color: #374151;
  font-weight: 400;
  font-size: 0.8rem;
  margin: 0 4px;
}

/* Responsividade */
@media (max-width: 1024px) {
  .standard-pagination-container {
    padding: 6px 10px;
  }
  
  .pagination-content {
    gap: 12px;
  }
  
  .pagination-text {
    font-size: 0.75rem;
  }
}

@media (max-width: 768px) {
  .standard-pagination-container {
    padding: 8px 10px;
  }
  
  .pagination-content {
    grid-template-columns: 1fr;
    gap: 8px;
    text-align: center;
  }
  
  .pagination-info,
  .pagination-summary,
  .pagination-controls {
    justify-self: center;
  }
  
  .pagination-info {
    order: 1;
  }
  
  .pagination-summary {
    order: 2;
  }
  
  .pagination-controls {
    order: 3;
  }
  
  /* Esconder botões circulares e mostrar links de texto */
  .pagination-buttons-desktop {
    display: none;
  }
  
  .pagination-links-mobile {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .pagination-select {
    min-width: 70px;
  }
  
  .pagination-text {
    font-size: 0.75rem;
  }
}

@media (max-width: 640px) {
  .standard-pagination-container {
    padding: 6px 8px;
  }
  
  .pagination-content {
    gap: 8px;
  }
  
  .pagination-link {
    font-size: 0.75rem;
    padding: 3px 6px;
  }
  
  .pagination-text {
    font-size: 0.7rem;
  }
}

@media (max-width: 480px) {
  .standard-pagination-container {
    padding: 6px 8px;
  }
  
  .pagination-content {
    gap: 6px;
  }
  
  .pagination-info {
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }
  
  .pagination-link {
    font-size: 0.7rem;
    padding: 2px 4px;
  }
  
  .pagination-text {
    font-size: 0.65rem;
  }
  
  .pagination-select {
    min-width: 60px;
  }
  
  .pagination-select :deep(.v-input__control) {
    min-height: 28px;
  }
}

@media (max-width: 360px) {
  .standard-pagination-container {
    padding: 4px 6px;
  }
  
  .pagination-content {
    gap: 4px;
  }
  
  .pagination-links-mobile {
    gap: 2px;
  }
  
  .pagination-link {
    font-size: 0.65rem;
    padding: 1px 3px;
  }
  
  .pagination-text {
    font-size: 0.6rem;
  }
  
  .pagination-select {
    min-width: 50px;
  }
}
</style>
